import React, { lazy } from "react";
import type { UiInteractionSpec, UiInteractionKind } from "../../types/state";
import type { UiInteractionValue } from "../../types/events";

export interface UiInteractionRendererProps {
  interaction: UiInteractionSpec;
  disabled: boolean;
  /**
   * True when there's at least one prior answered interaction in this
   * session.  Used by single_select to decide whether to show the
   * coaching feedback phase (explanation as post-answer feedback +
   * Continue button) before revealing the next question.
   */
  hasPriorInteraction: boolean;
  onSubmit: (value: UiInteractionValue, displayText: string) => void;
}

/**
 * The work-lane kinds.  ``coach_message`` / ``learner_message`` /
 * ``concept_diagram`` / ``custom_svg`` are thread kinds rendered by
 * AskModal and DeepDiveView by hand — the coach produces them into a
 * deep-dive scope, never into the activity lane, and none of them has
 * anything to submit, so the registry excludes them.
 */
export type RenderableInteractionKind = Exclude<
  UiInteractionKind,
  "coach_message" | "learner_message" | "concept_diagram" | "custom_svg"
>;

/**
 * Registry of UI interaction renderers, keyed by kind.
 *
 * Adding a new kind to UiInteractionSpec without registering a renderer
 * here will produce a TypeScript compile error.
 */
export const UI_INTERACTION_RENDERERS: Record<
  RenderableInteractionKind,
  React.LazyExoticComponent<React.FC<UiInteractionRendererProps>>
> = {
  single_select: lazy(
    () => import("./interactions/SingleSelectInteraction")
  ),
  free_text: lazy(
    () => import("./interactions/FreeTextInteraction")
  ),
  code: lazy(
    () => import("./interactions/CodeInteraction")
  ),
  sequence_gap_select: lazy(
    () => import("./interactions/SequenceGapSelectInteraction")
  ),
  ordering: lazy(
    () => import("./interactions/OrderingInteraction")
  ),
  manim_animation: lazy(
    () => import("./interactions/ManimAnimationInteraction")
  ),
  diagram_puzzle: lazy(
    () => import("./interactions/DiagramPuzzleInteraction")
  ),
  authored_activity: lazy(
    () => import("./interactions/ActivityHost")
  ),
};
